import type { ArtworkDocument } from '../core/types';
import type { ArtworkStore } from './ArtworkStore';
import { parseDocument, serializeDocument } from './schema';

/**
 * IndexedDB-backed ArtworkStore. Hand-rolled (no external dependency) to keep
 * the offline bundle small. A single database with one object store holds one
 * record under a fixed key.
 *
 * Robustness: all reads validate through `parseDocument`; corrupt or foreign
 * data resolves to null instead of throwing. Open failures (e.g. private mode)
 * reject, and the caller degrades to in-memory operation.
 */
const DB_NAME = 'ocean-color-game';
const DB_VERSION = 1;
const STORE = 'artwork';
const KEY = 'current';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this environment.'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Failed to open IndexedDB.'));
    req.onblocked = () => reject(new Error('IndexedDB open blocked.'));
  });
}

export class IndexedDbStore implements ArtworkStore {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) this.dbPromise = openDb();
    return this.dbPromise;
  }

  async load(): Promise<ArtworkDocument | null> {
    let db: IDBDatabase;
    try {
      db = await this.db();
    } catch {
      return null; // no IDB (private mode / unsupported) -> behave as empty
    }
    const raw = await new Promise<unknown>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }).catch(() => undefined);

    if (raw === undefined || raw === null) return null;
    // parseDocument returns null for any invalid/corrupt/incompatible record.
    return parseDocument(raw);
  }

  async save(doc: ArtworkDocument): Promise<void> {
    const db = await this.db();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(serializeDocument(doc), KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  async clear(): Promise<void> {
    const db = await this.db();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}

/** In-memory fallback used when IndexedDB is unavailable. */
export class MemoryStore implements ArtworkStore {
  private record: unknown = null;
  async load(): Promise<ArtworkDocument | null> {
    return this.record === null ? null : parseDocument(this.record);
  }
  async save(doc: ArtworkDocument): Promise<void> {
    this.record = serializeDocument(doc);
  }
  async clear(): Promise<void> {
    this.record = null;
  }
}
